import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";

import { Section, SectionHeader } from "@/components/site/Section";
import { getCourse } from "@/content/courses";
import { coursesWithDiscussionQuestions, sessionsWithQuestions } from "@/content/watch-party";
import {
  listMyWatchParties,
  startWatchParty,
  type WatchParty,
} from "@/utils/watch-party.functions";

export const Route = createFileRoute("/_authenticated/watch-party/")({
  head: () => ({
    meta: [
      { title: "Watch Party Facilitator Kit | Leadership Edge Live" },
      {
        name: "description",
        content:
          "Run a watch party with your team: discussion questions, a soft timer, and room-level themes.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Watch Party Facilitator Kit" },
      { property: "og:description", content: "Facilitate a leader-led session with your team." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: WatchPartyStart,
});

function WatchPartyStart() {
  const load = useServerFn(listMyWatchParties);
  const begin = useServerFn(startWatchParty);
  const navigate = useNavigate();

  const available = useMemo(() => coursesWithDiscussionQuestions(), []);
  const [courseSlug, setCourseSlug] = useState(available[0]?.slug ?? "");
  const [sessionIndex, setSessionIndex] = useState<string>("");
  const [attendees, setAttendees] = useState("6");
  const [parties, setParties] = useState<WatchParty[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessions = useMemo(
    () => (courseSlug ? sessionsWithQuestions(courseSlug) : []),
    [courseSlug],
  );

  useEffect(() => {
    load()
      .then(setParties)
      .catch(() => setParties([]));
  }, [load]);

  useEffect(() => setSessionIndex(""), [courseSlug]);

  function start() {
    setBusy(true);
    setError(null);
    begin({
      data: {
        courseSlug,
        sessionIndex: sessionIndex === "" ? null : Number(sessionIndex),
        attendeeCount: Number(attendees) || 1,
      },
    })
      .then(({ partyId }) => navigate({ to: "/watch-party/$partyId/", params: { partyId } }))
      .catch(() => setError("We couldn't start that watch party. Try again."))
      .finally(() => setBusy(false));
  }

  return (
    <Section>
      <SectionHeader
        eyebrow="Watch Party"
        title="Run a session with your team"
        intro="Pick the course you're watching together, tell us how many people are in the room, and we'll hand you the questions one at a time."
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
      />

      <div className="rounded-2xl border border-border/60 bg-background p-6 md:p-7">
        {available.length === 0 ? (
          <p className="text-sm text-ink">
            Discussion questions are being written course by course. None are ready yet.{" "}
            <Link to="/course-catalog/" className="font-semibold text-primary underline">
              Browse the catalog
            </Link>
          </p>
        ) : (
          <div className="space-y-5">
            <div>
              <label htmlFor="wp-course" className="text-sm font-semibold text-ink">
                Course
              </label>
              <select
                id="wp-course"
                value={courseSlug}
                onChange={(e) => setCourseSlug(e.target.value)}
                className="mt-2 w-full rounded-[6px] border border-border bg-background px-4 py-4 text-base text-ink"
              >
                {available.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.title}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-sm text-muted-foreground">
                Only courses with discussion questions written are listed. We're adding more.
              </p>
            </div>

            {sessions.length > 0 && (
              <div>
                <label htmlFor="wp-session" className="text-sm font-semibold text-ink">
                  Session
                </label>
                <select
                  id="wp-session"
                  value={sessionIndex}
                  onChange={(e) => setSessionIndex(e.target.value)}
                  className="mt-2 w-full rounded-[6px] border border-border bg-background px-4 py-4 text-base text-ink"
                >
                  <option value="">Whole course</option>
                  {sessions.map(({ index, session }) => (
                    <option key={index} value={String(index)}>
                      {session.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label htmlFor="wp-attendees" className="text-sm font-semibold text-ink">
                People in the room
              </label>
              <input
                id="wp-attendees"
                type="number"
                min={1}
                max={200}
                value={attendees}
                onChange={(e) => setAttendees(e.target.value)}
                className="mt-2 w-full rounded-[6px] border border-border bg-background px-4 py-4 text-base text-ink"
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <button
              type="button"
              disabled={busy || !courseSlug}
              onClick={start}
              className="w-full rounded-[6px] bg-[#693D94] px-6 py-4 text-base font-semibold text-primary-foreground shadow-brand disabled:opacity-60"
            >
              {busy ? "Starting…" : "Start the watch party"}
            </button>
          </div>
        )}
      </div>

      {parties && parties.length > 0 && (
        <div className="mt-10">
          <h3 className="font-display text-xl font-semibold text-ink">Your recent watch parties</h3>
          <ul className="mt-4 space-y-3">
            {parties.map((p) => (
              <li
                key={p.id}
                className="flex flex-col gap-3 rounded-2xl border border-border/60 bg-background p-5 sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <p className="font-semibold text-ink">
                    {getCourse(p.courseSlug)?.title ?? p.courseSlug}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(p.startedAt).toLocaleDateString()} · {p.attendeeCount} in the room
                  </p>
                </div>
                <div className="flex gap-3">
                  <Link
                    to="/watch-party/$partyId/"
                    params={{ partyId: p.id }}
                    className="rounded-[6px] border border-border px-4 py-3 text-sm font-semibold text-ink"
                  >
                    Open
                  </Link>
                  <Link
                    to="/watch-party/$partyId/recap/"
                    params={{ partyId: p.id }}
                    className="rounded-[6px] bg-[#693D94] px-4 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Recap
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}
